import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tag, Message } from '@arco-design/web-react';
import { IconThumbUp, IconThumbDown } from '@arco-design/web-react/icon';
import { RootState } from '@/store';
import { setFeedback, setHistoryMsg, closeKeywordPopup, setKeywordPopup } from '@/store/slices/room';
import { isMobile } from '@/utils/utils';
import { useScene } from '@/lib/useCommon';
import RtcClient from '@/lib/RtcClient';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import type { EnhancedSuggestionQuestion } from '@/utils/suggestionManager';
import { suggestionManager } from '@/utils/suggestionManager';
import SuggestionQuestions from '@/components/SuggestionQuestions';
import KeywordPopup from '@/components/KeywordPopup';

import styles from './index.module.less';

import logger from '@/utils/logger';

// 控制对话框旁边头像显示的开关
const SHOW_AVATAR = false;

// 动画提示框
function FeedbackTip({ show }: { show: boolean }) {
  return (
    <div className={`${styles.feedbackTip} ${show ? styles.show : ''}`}>
      哎呀，我哪里做得不好？告诉我吧。
    </div>
  );
}

const Conversation = React.memo(function Conversation(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className, ...rest } = props;
  const dispatch = useDispatch();

  // 优化selector使用
  const { msgHistory, isAITalking, userId, activeKeywordPopups } = useSelector(
    (state: RootState) => ({
      msgHistory: state.room.msgHistory,
      isAITalking: state.room.isAITalking,
      userId: state.room.localUser.userId,
      activeKeywordPopups: state.room.activeKeywordPopups,
    }),
    // 浅比较优化
    (left, right) =>
      left.msgHistory === right.msgHistory &&
      left.isAITalking === right.isAITalking &&
      left.userId === right.userId &&
      left.activeKeywordPopups === right.activeKeywordPopups
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const sceneData = useScene();
  const { botName, icon, id: sceneId } = sceneData;

  // 优化场景配置缓存
  const sceneConfig = useMemo(() => sceneData, [sceneData.id, sceneData.name, sceneData.botName]);

  // 关闭关键词弹窗（传入title关闭指定弹窗，不传则关闭所有）
  const handleCloseKeywordPopup = useCallback(
    (title?: string) => {
      dispatch(closeKeywordPopup(title as any));
    },
    [dispatch]
  );

  // 暴露测试函数到 window（开发用）
  useEffect(() => {
    // 测试单个弹窗
    (window as any).__testKeywordPopup = (index = 0) => {
      const popups = sceneData.keywordPopups;
      if (popups && popups.length > 0) {
        const popup = popups[index] || popups[0];
        dispatch(setKeywordPopup(popup));
        logger.info('[Conversation] 测试弹窗:', popup);
      } else {
        logger.warn('[Conversation] 没有可用的关键词弹窗配置，当前场景:', sceneData.id);
      }
    };
    // 测试多个弹窗
    (window as any).__testKeywordPopups = (...indices: number[]) => {
      const popups = sceneData.keywordPopups;
      if (popups && popups.length > 0) {
        const selectedPopups = indices.length > 0
          ? indices.map(i => popups[i]).filter(Boolean)
          : popups;
        dispatch(setKeywordPopup(selectedPopups));
        logger.info('[Conversation] 测试多弹窗:', selectedPopups);
      } else {
        logger.warn('[Conversation] 没有可用的关键词弹窗配置，当前场景:', sceneData.id);
      }
    };
    return () => {
      delete (window as any).__testKeywordPopup;
      delete (window as any).__testKeywordPopups;
    };
  }, [sceneData, dispatch]);

  const [isFeedbackListening, setIsFeedbackListening] = useState(false);
  const [showFeedbackTip, setShowFeedbackTip] = useState(false);
  const feedbackTipTimer = useRef<NodeJS.Timeout | null>(null);
  const prevMsgLen = useRef(msgHistory.length);

  // 联想问题功能
  const [suggestionQuestions, setSuggestionQuestions] = useState<EnhancedSuggestionQuestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const hasGeneratedWelcomeSuggestion = useRef(false);

  // ========= 自动整体上移动画 =========
  // （已移除整体上移动画逻辑）

  // 处理联想问题点击 - 使用useCallback优化
  const handleSuggestionClick = useCallback(
    async (questionText: string) => {
      if (!userId) return;

      dispatch(setHistoryMsg({ text: questionText, user: userId, paragraph: true }));

      try {
        await RtcClient.commandAgent({
          command: COMMAND.EXTERNAL_TEXT_TO_LLM,
          agentName: botName,
          message: questionText,
          interruptMode: INTERRUPT_PRIORITY.HIGH,
        });
      } catch (error) {
        logger.error('[Conversation] handleSuggestionClick error:', error);
      }

      // 隐藏联想问题
      setShowSuggestions(false);
    },
    [dispatch, userId, botName]
  );

  // 只在AI开始说话时递增key
  const [aiBubbleAnimKey, setAiBubbleAnimKey] = useState(0);
  const prevIsAITalking = useRef(isAITalking);
  useEffect(() => {
    if (!prevIsAITalking.current && isAITalking) {
      setAiBubbleAnimKey((k) => k + 1);
    }
    prevIsAITalking.current = isAITalking;
  }, [isAITalking]);

  // 监听消息历史变化，智能刷新联想问题（只在AI消息定稿且为最新时刷新）
  useEffect(() => {
    if (msgHistory.length === 0) {
      hasGeneratedWelcomeSuggestion.current = false; // 新会话重置
      return;
    }
    const lastMsg = msgHistory[msgHistory.length - 1];
    // 只有最后一条消息是AI且定稿，才刷新建议问题
    if (lastMsg.user === botName && !lastMsg.isInterrupted) {
      const aiMsgCount = msgHistory.reduce((acc, m) => acc + (m.user === botName ? 1 : 0), 0);
      const isFirstAIMessage = aiMsgCount === 1;
      // 欢迎语（第一条AI消息）仅显示预设问题，不调用 LLM
      if (isFirstAIMessage && lastMsg.paragraph) {
        suggestionManager
          .getWelcomeSuggestions(sceneConfig)
          .then((welcome) => {
            setSuggestionQuestions(welcome);
            setShowSuggestions(welcome.length > 0);
            hasGeneratedWelcomeSuggestion.current = true;
          })
          .catch((error) => {
            logger.error('[Conversation] 获取欢迎语建议问题失败:', error);
          });
        return;
      }

      // 到段落结束时：调用统一管理器生成联想问题
      if (lastMsg.paragraph && lastMsg.value && lastMsg.value.length > 5) {
        // 构建对话历史
        const conversationHistory = msgHistory
          .filter((m) => m.user === botName || m.user === userId)
          .map((m) => ({
            user: m.user,
            value: m.value,
            role: (m.user === botName ? 'assistant' : 'user') as 'user' | 'assistant',
          }));

        suggestionManager
          .getConversationSuggestions(lastMsg.value, conversationHistory, sceneConfig)
          .then((suggestions) => {
            setSuggestionQuestions(suggestions);
            setShowSuggestions(suggestions.length > 0);
          })
          .catch((error) => {
            logger.error('[Conversation] 获取对话建议问题失败:', error);
          });
      }
      // 原欢迎语随机推荐逻辑已移除：不在第一句欢迎语展示联想问题
    }
    // 注意：移除了原来的 else 分支，不再在用户发送消息时立即隐藏建议问题
  }, [msgHistory, botName, sceneId, sceneConfig, userId]);

  // 当AI开始新一轮说话时，隐藏之前的建议问题
  useEffect(() => {
    // 如果AI从不说话状态变为说话状态，说明开始了新一轮回答
    if (!prevIsAITalking.current && isAITalking) {
      logger.info('[Conversation] AI开始新一轮回答，隐藏之前的建议问题');
      setShowSuggestions(false);
      setSuggestionQuestions([]);
    }
    prevIsAITalking.current = isAITalking;
  }, [isAITalking]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [msgHistory.length]);

  // 推荐问题出现时，平滑滚动到顶部确保气泡可见
  useEffect(() => {
    if (showSuggestions && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showSuggestions]);

  // 处理不满意按钮点击
  const handleDislikeClick = () => {
    dispatch(setFeedback({ msgIndex: -1, feedback: 'dislike' }));
    // 打断AI
    RtcClient.commandAgent({
      agentName: botName,
      command: COMMAND.INTERRUPT,
    });
    // 弹出带动画的提示
    setShowFeedbackTip(true);
    if (feedbackTipTimer.current) clearTimeout(feedbackTipTimer.current);
    feedbackTipTimer.current = setTimeout(() => setShowFeedbackTip(false), 2500);
    // 朗读提示
    RtcClient.commandAgent({
      command: COMMAND.EXTERNAL_TEXT_TO_SPEECH,
      agentName: botName,
      message: '哎呀，我哪里做得不好？告诉我吧。',
      interruptMode: INTERRUPT_PRIORITY.HIGH,
    });
    // 进入反馈监听状态（不再主动开启音频采集）
    setIsFeedbackListening(true);
  };

  // 组件卸载时清理提示定时器，避免潜在内存泄漏
  useEffect(() => {
    return () => {
      if (feedbackTipTimer.current) {
        clearTimeout(feedbackTipTimer.current);
        feedbackTipTimer.current = null;
      }
    };
  }, []);

  // 监听msgHistory变化，捕获反馈内容
  useEffect(() => {
    if (!isFeedbackListening) return;
    const lastMsg = msgHistory[msgHistory.length - 1];
    if (
      msgHistory.length > prevMsgLen.current &&
      lastMsg &&
      lastMsg.user === userId &&
      lastMsg.definite // 只捕获最终ASR结果
    ) {
      // 结束ASR
      RtcClient.stopAudioCapture?.();
      // 保存为反馈（加feedback: 'dislike'，并设为段落）
      dispatch(
        setHistoryMsg({
          text: lastMsg.value,
          user: userId,
          paragraph: true,
          feedback: 'dislike',
        })
      );
      setIsFeedbackListening(false);
      // 模拟点击"退出通话"按钮
      const leaveBtn = document.querySelector("img[alt='leave']") as HTMLElement;
      if (leaveBtn) {
        leaveBtn.click();
      }
    }
    prevMsgLen.current = msgHistory.length;
  }, [msgHistory, isFeedbackListening, userId, dispatch]);

  // 只显示最后一轮AI和用户消息（使用 useMemo 优化，避免每次渲染都遍历数组）
  const { displayMsgs, aiMsg } = useMemo(() => {
    if (msgHistory.length === 0) return { displayMsgs: [], aiMsg: null };

    const lastAIMsgIndex = [...msgHistory].reverse().findIndex((msg) => msg.user === botName);
    const lastUserMsgIndex = [...msgHistory].reverse().findIndex((msg) => msg.user === userId);
    const aiMsg = lastAIMsgIndex !== -1 ? msgHistory[msgHistory.length - 1 - lastAIMsgIndex] : null;
    const userMsg =
      lastUserMsgIndex !== -1 ? msgHistory[msgHistory.length - 1 - lastUserMsgIndex] : null;

    return { displayMsgs: [aiMsg, userMsg].filter(Boolean), aiMsg };
  }, [msgHistory, botName, userId]);

  return (
    <>
      <FeedbackTip show={showFeedbackTip} />
      {/* 关键词弹窗 - 左侧多缩略图模式，点击展开大屏（带标签栏切换所有配置的图片集） */}
      <KeywordPopup
        configs={activeKeywordPopups}
        allConfigs={sceneData.keywordPopups}
        onClose={handleCloseKeywordPopup}
      />
      <div
        ref={containerRef}
        className={`${styles.conversationGrid} ${styles.conversationOverlay} ${className} ${
          isMobile() ? styles.mobileConversation : ''
        }`}
        {...rest}
      >
        {/*
        {!isAIReady && (
          <div
            className={styles.aiReadyingBox}
            style={{ left: '50%', top: '25%', transform: 'translate(-50%, -50%)' }}
          >
            <Spin size={20} className={styles['aiReading-spin']} />
            AI 准备中，请稍候
          </div>
        )}
        */}
        {displayMsgs.map((msg, index) => {
          if (!msg) return null;
          const { value, user, isInterrupted, feedback } = msg;
          const isUserMsg = user === userId;
          const isRobotMsg = user === botName;

          if (!isUserMsg && !isRobotMsg) {
            return null;
          }

          const rowStyle: React.CSSProperties = {
            gridColumn: isUserMsg ? '3 / 4' : '1 / 2',
            justifySelf: isUserMsg ? 'end' : 'start',
          };

          return (
            <div
              key={`msg-row-${index}`}
              className={`${styles.messageRow} ${isUserMsg ? styles.userMessageRow : ''}`}
              style={rowStyle}
            >
              {SHOW_AVATAR && !isUserMsg && (
                <div className={styles.avatarAreaSmall}>
                  <img src={icon} alt="AI Avatar" />
                </div>
              )}
              <div className={styles.bubbleWrapper}>
                {isRobotMsg ? (
                  <div
                    key={aiBubbleAnimKey}
                    className={`${styles.bubbleFrame} ${styles.aiBubbleAnim}`}
                  >
                    <div className={styles.cloudDecor}>
                      <div className={styles.cloudDot1} />
                      <div className={styles.cloudDot2} />
                      <div className={styles.cloudDot3} />
                    </div>
                    <div
                      className={`${styles.bubble} ${styles.aiBubble}`}
                      ref={(el) => {
                        if (isRobotMsg && el) {
                          el.scrollTop = el.scrollHeight; // 滚动到底部
                        }
                      }}
                    >
                      <div className={styles.content}>{value}</div>
                      {isInterrupted && <Tag className={styles.interruptTag}>已打断</Tag>}
                    </div>
                  </div>
                ) : (
                  <div
                    key={msg.paragraph ? (msg.time || '') + user : user}
                    className={`${styles.bubble} ${styles.userBubble} ${styles.userBubbleAnim}`}
                  >
                    <div className={styles.content}>{value}</div>
                  </div>
                )}
                {isRobotMsg && msg.paragraph && (
                  <>
                    <div className={styles.feedbackBox}>
                      <button
                        className={`${styles.feedbackBtn} ${styles.like} ${
                          feedback === 'like' ? styles.likeActive : ''
                        }`}
                        onClick={() => {
                          // 使用在 msgHistory 中的真实索引，确保点赞正确记录
                          const realIndex = msgHistory.indexOf(msg as any);
                          dispatch(setFeedback({ msgIndex: realIndex, feedback: 'like' }));
                          Message.success('谢谢你的鼓励！');
                        }}
                        disabled={!!feedback}
                      >
                        <IconThumbUp />
                      </button>
                      <button
                        className={`${styles.feedbackBtn} ${styles.dislike} ${
                          feedback === 'dislike' ? styles.dislikeActive : ''
                        }`}
                        onClick={handleDislikeClick}
                        disabled={!!feedback}
                      >
                        <IconThumbDown />
                      </button>
                    </div>
                    {/* 联想问题组件 */}
                    {showSuggestions && isRobotMsg && msg === aiMsg && !msg.isInterrupted && (
                      <SuggestionQuestions
                        key={aiBubbleAnimKey}
                        questions={suggestionQuestions}
                        onQuestionClick={handleSuggestionClick}
                        visible={showSuggestions}
                      />
                    )}
                  </>
                )}
              </div>
              {SHOW_AVATAR && isUserMsg && (
                <div className={styles.avatarAreaSmall}>
                  {/* <img src={USER_AVATAR} alt="User Avatar" /> */}
                  <div className={styles.defaultAvatar}>用户</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
});

export default Conversation;
